import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import "./drift-wall.css";

export type DriftWallProps = {
  images: string[];
  columns?: number;
  tileWidth?: number;
  tileHeight?: number;
  gap?: number;
  radius?: number;
  /** rotateX of the plane, in degrees. */
  tilt?: number;
  /** rotateY of the plane, in degrees. */
  turn?: number;
  /** rotateZ of the plane, in degrees. */
  roll?: number;
  perspective?: number;
  /** How far the plane is pushed away from the camera, in px. */
  depth?: number;
  /** Base scroll speed, px/s. */
  speed?: number;
  direction?: "up" | "down";
  /** 0–1 spread of per-column speed jitter. */
  variance?: number;
  /** 0–1 strength of the pointer-driven plane tilt. */
  parallax?: number;
  /** How far a hovered tile pops toward the camera, in px. */
  lift?: number;
  /** 0–1 size of the soft edge mask. */
  fade?: number;
  /** Resting opacity of idle tiles. */
  dim?: number;
  grayscale?: boolean;
  overlayColor?: string;
  pauseOnHover?: boolean;
  className?: string;
};

/** Deterministic per-column speed jitter, walked by the golden ratio so columns never sync up. */
const colFactor = (i: number, variance: number) => 1 + variance * ((((i * 0.6180339887 + 0.35) % 1) * 2) - 1);

export function DriftWall({
  images,
  columns = 5,
  tileWidth = 200,
  tileHeight = 132,
  gap = 18,
  radius = 14,
  tilt = 16,
  turn = -14,
  roll = 0,
  perspective = 1200,
  depth = 120,
  speed = 42,
  direction = "up",
  variance = 0.45,
  parallax = 0.6,
  lift = 64,
  fade = 0.6,
  dim = 0.55,
  grayscale = false,
  overlayColor = "#060010",
  pauseOnHover = false,
  className = "",
}: DriftWallProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const planeRef = useRef<HTMLDivElement | null>(null);
  const trackRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Tile count depends on how tall the wall is, so it is measured rather than assumed.
  // Only coarse changes rebuild the DOM — small resizes just ride the existing tiles.
  const [height, setHeight] = useState(600);

  const cols = useMemo(() => {
    const count = Math.max(1, Math.round(columns) || 1);
    const out: string[][] = Array.from({ length: count }, () => []);
    images.forEach((src, i) => out[i % count]!.push(src));
    return out;
  }, [images, columns]);

  const unit = tileHeight + gap;
  const meta = useMemo(
    () =>
      cols.map(col => {
        const copyHeight = Math.max(unit, col.length * unit);
        return { copyHeight, copies: Math.max(2, Math.ceil((height * 1.6) / copyHeight) + 1) };
      }),
    [cols, unit, height],
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height || 600;
      setHeight(prev => (Math.abs(h - prev) > 40 ? h : prev));
    });
    ro.observe(root);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    const plane = planeRef.current;
    if (!root || !plane) return;

    const dirSign = direction === "up" ? 1 : -1;
    // Alternating sign sends neighbouring columns in opposite directions.
    const base = cols.map((_, i) => speed * colFactor(i, variance) * dirSign * (i % 2 === 0 ? 1 : -1));
    const offsets = meta.map((m, i) => m.copyHeight * ((i * 0.37) % 1));
    const vels = cols.map(() => 0);

    const reduce = matchMedia("(prefers-reduced-motion: reduce)");
    const ptr = { x: 0, y: 0 };
    const ptrD = { x: 0, y: 0 };
    let hoverCol = -1;
    let wallHover = false;
    let activeTile: HTMLElement | null = null;
    let last: number | null = null;
    let raf = 0;

    const release = () => {
      activeTile?.classList.remove("is-active");
      activeTile = null;
      hoverCol = -1;
    };

    // Listening on the window rather than the wall itself: the content layer sits on top of
    // the wall, so pointer events would otherwise never reach it.
    const onMove = (e: PointerEvent) => {
      const r = root.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      wallHover = inside;
      if (!inside) {
        ptr.x = 0;
        ptr.y = 0;
        release();
        return;
      }
      if (parallax > 0 && !reduce.matches) {
        ptr.x = (e.clientX - r.left) / r.width - 0.5;
        ptr.y = (e.clientY - r.top) / r.height - 0.5;
      }
      const hit = document.elementFromPoint(e.clientX, e.clientY);
      const tile = hit?.closest<HTMLElement>(".dw-tile") ?? null;
      if (tile === activeTile) return;
      release();
      if (!tile) return;
      activeTile = tile;
      hoverCol = Number(tile.dataset.col);
      tile.classList.add("is-active");
    };

    const onLeave = () => {
      wallHover = false;
      ptr.x = 0;
      ptr.y = 0;
      release();
    };

    const tick = (ts: number) => {
      if (last === null) last = ts;
      const dt = Math.min(0.05, Math.max(0, ts - last) / 1000);
      last = ts;

      const maxTilt = parallax * 8;
      const damp = 1 - Math.exp(-dt / 0.12);
      ptrD.x += (ptr.x * maxTilt - ptrD.x) * damp;
      ptrD.y += (-ptr.y * maxTilt - ptrD.y) * damp;
      plane.style.transform = `translate(-50%,-50%) scale(1.18) rotateX(${tilt + ptrD.y}deg) rotateY(${turn + ptrD.x}deg) rotateZ(${roll}deg) translateZ(${-depth}px)`;

      const reduced = reduce.matches;
      for (let i = 0; i < meta.length; i++) {
        const track = trackRefs.current[i];
        const m = meta[i];
        if (!track || !m) continue;
        if (!reduced) {
          const paused = (wallHover && pauseOnHover) || hoverCol === i;
          const target = paused ? 0 : base[i]!;
          const ease = 1 - Math.exp(-dt / (target === 0 ? 0.16 : 0.28));
          const vel = vels[i]! + (target - vels[i]!) * ease;
          vels[i] = vel;
          const next = offsets[i]! + vel * dt;
          offsets[i] = ((next % m.copyHeight) + m.copyHeight) % m.copyHeight;
        }
        track.style.transform = `translate3d(0,${-offsets[i]!}px,0)`;
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
      release();
    };
  }, [cols, meta, tilt, turn, roll, depth, speed, direction, variance, parallax, pauseOnHover]);

  const vars = {
    "--dw-tile-w": `${tileWidth}px`,
    "--dw-tile-h": `${tileHeight}px`,
    "--dw-gap": `${gap}px`,
    "--dw-radius": `${radius}px`,
    "--dw-perspective": `${perspective}px`,
    "--dw-lift": `${lift}px`,
    "--dw-dim": dim,
    "--dw-gray": grayscale ? 1 : 0,
    "--dw-overlay": overlayColor,
    "--dw-edge": `${Math.max(0, (1 - fade) * 100)}%`,
  } as CSSProperties;

  const restingPlane = `translate(-50%,-50%) scale(1.18) rotateX(${tilt}deg) rotateY(${turn}deg) rotateZ(${roll}deg) translateZ(${-depth}px)`;

  return (
    <div ref={rootRef} className={`dw-root ${className}`} style={vars} aria-hidden="true">
      <div ref={planeRef} className="dw-plane" style={{ transform: restingPlane }}>
        {cols.map((col, ci) => (
          <div className="dw-col" key={ci}>
            <div
              className="dw-track"
              style={{ transform: `translate3d(0,${-(meta[ci]?.copyHeight ?? 0) * ((ci * 0.37) % 1)}px,0)` }}
              ref={el => {
                trackRefs.current[ci] = el;
              }}
            >
              {Array.from({ length: meta[ci]?.copies ?? 2 }, (_, k) =>
                col.map((src, ii) => (
                  <div className="dw-tile" data-col={ci} key={`${k}-${ii}`}>
                    <div className="dw-inner">
                      <img src={src} alt="" loading="lazy" decoding="async" draggable={false} />
                      <div className="dw-ov" />
                    </div>
                  </div>
                )),
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DriftWall;
